import moment from "moment";
import { Action } from "./action";
import { ActionBase } from "./actionBase";
import { CanceledAction } from "./canceledAction";
import { DiscontinuedAction } from "./discontinuedAction";
import { Order } from "./models";
import { OrderSignatureRequests } from "./orderSignatureRequests";

export interface OrderVisitFrequency {
  externalPatientChartId?: number;
  patientName?: string;
  orderType: string;
  tenantId?: number;
  nameDescription: string;
  createdAction?: ActionBase;
  modifiedAction?: ActionBase;
  discontinuedAction?: DiscontinuedAction;
  canceledAction?: CanceledAction;
  startDate: string;
  startDateDisplay?: string;
  discontinueDate?: string;
  discontinueDateDisplay?: string;
  categories?: Set<string>;
  categoryOtherDescription?: string;
  teamName?: string;
  teamId?: number;
  teamQbosId?: number;
  patientChartId?: number;
  orderId?: string;
  history?: Array<Action>;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdById?: number;
  updatedById?: number;
  migrationId?: number;
  signatureRequests?: OrderSignatureRequests;
  orderingProviderId?: number;
  orderingProviderDisplayName: string;
  providerNotes?: string;
  approvalType: Order.ApprovalTypeEnum;
  readBackAndVerified?: boolean;
  discipline?: string;
  PRNReasons?: string;
  PRNQuantity?: number;
  visits: number;
  timeInterval: OrderVisitFrequency.TimeIntervalEnum;
  duration: number;
  declined?: boolean;
  declinedDate?: string;
  declinedReason?: string;
}

export namespace OrderVisitFrequency {
  export type TimeIntervalEnum = "DAY" | "WEEK" | "2_WEEKS" | "MONTH";
  export const TimeIntervalEnum = {
    Day: "DAY" as TimeIntervalEnum,
    Week: "WEEK" as TimeIntervalEnum,
    TwoWeeks: "2_WEEKS" as TimeIntervalEnum,
    Month: "MONTH" as TimeIntervalEnum,
  };

  export interface PRNOnlyOrderFields {
    PRN?: boolean;
    visits?: number;
    timeInterval?: string;
    duration?: number;
    durationInterval?: string;
  }

  /**
   * Returns true if PRN and all visit frequency fields are blank:
   *    Visits (visits)
   *    Frequency (timeInterval)
   *    Duration (duration)
   *    Duration Timeframe (durationInterval)
   *
   * PRN-only orders bypass certain checks.
   *
   * @param orderFormState
   * @returns
   */
  export function isPRNOnlyOrder(order: PRNOnlyOrderFields) {
    const { PRN, visits, timeInterval, duration, durationInterval } = order;
    return PRN && !visits && !timeInterval && !duration && !durationInterval;
  }

  /**
   * Discontinue dates will snap based on the durationInterval.
   * For "DAY" they will snap to the end of the day.
   * For "WEEK" they will snap to the following Saturday. The first week is the current week (index at 0).
   * For "MONTH" they will snap to the end of the month. The first month is the current month (index at 0).
   *
   * @param startDate
   * @param durationInterval
   * @param duration
   * @returns the discontinue date
   */
  export function calculateDiscontinueDate(
    startDate: Date,
    durationInterval: string,
    duration: number
  ): Date | null {
    const mBeginningOfStartDate = moment(startDate).startOf("day");
    if (durationInterval && duration && startDate) {
      let mDiscontinueDate;
      switch (durationInterval) {
        case "DAY":
          mDiscontinueDate = mBeginningOfStartDate
            .add(duration - 1, "days")
            .endOf("day");
          break;
        case "WEEK":
          mDiscontinueDate = mBeginningOfStartDate
            .add(duration - 1, "weeks")
            .endOf("week");
          break;
        case "2_WEEKS":
          mDiscontinueDate = mBeginningOfStartDate
            .add(duration * 2 - 1, "weeks")
            .endOf("week");
          break;
        case "MONTH":
          mDiscontinueDate = mBeginningOfStartDate
            .add(duration - 1, "months")
            .endOf("month");
          break;
        default:
          throw new Error("Invalid time interval");
      }
      // Roll beginning of day back to end of previous day
      return mDiscontinueDate.toDate();
    }
    return null;
  }

  export function getIntervalDescription(
    interval: string,
    quantity: number = 0
  ): string {
    switch (interval) {
      case "DAY":
        return `Day${quantity > 1 ? "s" : ""}`;
      case "WEEK":
        return `Week${quantity > 1 ? "s" : ""}`;
      case "2_WEEKS":
        return "Other Week";
      case "MONTH":
        return `Month${quantity > 1 ? "s" : ""}`;
      default:
        return "";
    }
  }
}



// WEBPACK FOOTER //
// ./src/models/order-management/orderVisitFrequency.ts